import { useState, useEffect, useCallback } from "react";
import { 
  Shield, Plus, Trash2, Edit2, ExternalLink, 
  Loader2, CheckCircle, AlertCircle, GripVertical,
  Clock, FileText
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import axios from "axios";
import { toast } from "sonner";
import "./ComplianceSettings.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { value: "safety", label: "Safety & Training" },
  { value: "coaching", label: "Coaching Credentials" },
  { value: "medical", label: "Medical" },
  { value: "other", label: "Other" },
];

const ComplianceSettings = () => {
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "safety",
    validity_months: 24,
    required: true,
    external_url: "",
    instructions: ""
  });

  const getAuthHeaders = () => {
    return { Authorization: "Bearer cookie-session" };
  };

  const fetchRequirements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/compliance/requirements`, { headers: getAuthHeaders() });
      setRequirements(res.data.requirements || []);
      setDefaults(res.data.defaults_available || []);
    } catch (err) {
      console.error("Error fetching requirements:", err);
      toast.error("Failed to load compliance requirements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const handleInitDefaults = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/compliance/requirements/init-defaults`, {}, { headers: getAuthHeaders() });
      toast.success("Default requirements added");
      fetchRequirements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initialize defaults");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDialog = (req = null) => {
    if (req) {
      setEditingReq(req);
      setFormData({
        name: req.name || "",
        description: req.description || "",
        category: req.category || "safety",
        validity_months: req.validity_months || 24,
        required: req.required !== false,
        external_url: req.external_url || "",
        instructions: req.instructions || ""
      });
    } else {
      setEditingReq(null);
      setFormData({
        name: "",
        description: "",
        category: "safety",
        validity_months: 24,
        required: true,
        external_url: "",
        instructions: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Requirement name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingReq) {
        await axios.put(`${API}/compliance/requirements/${editingReq.requirement_id}`, formData, { headers: getAuthHeaders() });
        toast.success("Requirement updated");
      } else {
        await axios.post(`${API}/compliance/requirements`, formData, { headers: getAuthHeaders() });
        toast.success("Requirement created");
      }
      setDialogOpen(false);
      fetchRequirements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save requirement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (req) => {
    if (!confirm(`Delete "${req.name}"? This will remove it from all coaches.`)) return;
    
    try {
      await axios.delete(`${API}/compliance/requirements/${req.requirement_id}`, { headers: getAuthHeaders() });
      toast.success("Requirement deleted");
      fetchRequirements();
    } catch (err) {
      toast.error("Failed to delete requirement");
    }
  };

  const handleToggleRequired = async (req) => {
    try {
      await axios.put(`${API}/compliance/requirements/${req.requirement_id}`, {
        required: !req.required
      }, { headers: getAuthHeaders() });
      fetchRequirements();
    } catch (err) {
      toast.error("Failed to update requirement");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Compliance Requirements</h3>
          <p className="text-sm text-slate-500">
            Configure certifications and training requirements for coaches
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-requirement-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Requirement
        </Button>
      </div>

      {/* Empty State - Initialize Defaults */}
      {requirements.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <Shield className="w-12 h-12 text-blue-500 mx-auto mb-3" />
          <h3 className="font-semibold text-blue-900 mb-2">No Compliance Requirements Configured</h3>
          <p className="text-sm text-blue-700 mb-4">
            Start with our recommended defaults (SafeSport & Concussion Training) or create your own.
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={handleInitDefaults} disabled={saving} data-testid="init-defaults-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Add Recommended Defaults
            </Button>
            <Button variant="outline" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Custom
            </Button>
          </div>
        </div>
      )}

      {/* Requirements List */}
      {requirements.length > 0 && (
        <div className="space-y-3">
          {requirements.map((req) => (
            <div 
              key={req.requirement_id}
              className="bg-white border border-slate-200 rounded-xl p-4"
              data-testid={`requirement-${req.requirement_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    req.category === "safety" ? "bg-red-100" :
                    req.category === "coaching" ? "bg-blue-100" :
                    req.category === "medical" ? "bg-emerald-100" : "bg-slate-100"
                  }`}>
                    <Shield className={`w-5 h-5 ${
                      req.category === "safety" ? "text-red-600" :
                      req.category === "coaching" ? "text-blue-600" :
                      req.category === "medical" ? "text-emerald-600" : "text-slate-600"
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-900">{req.name}</h4>
                      {req.required && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{req.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Valid for {req.validity_months} months
                      </span>
                      {req.external_url && (
                        <a 
                          href={req.external_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Training Link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 mr-4">
                    <span className="text-xs text-slate-500">Required</span>
                    <Switch
                      checked={req.required}
                      onCheckedChange={() => handleToggleRequired(req)}
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleOpenDialog(req)}
                    data-testid={`edit-req-${req.requirement_id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDelete(req)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-req-${req.requirement_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add from Defaults */}
      {requirements.length > 0 && defaults.length > requirements.length && (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-500 mb-3">Add additional recommended requirements:</p>
          <div className="flex flex-wrap gap-2">
            {defaults
              .filter(d => !requirements.find(r => r.name === d.name))
              .map((def, i) => (
                <Button 
                  key={i} 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setEditingReq(null);
                    setFormData({
                      name: def.name,
                      description: def.description,
                      category: def.category,
                      validity_months: def.validity_months,
                      required: def.required,
                      external_url: def.external_url || "",
                      instructions: def.instructions || ""
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {def.name}
                </Button>
              ))
            }
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingReq ? "Edit Requirement" : "Add Compliance Requirement"}</DialogTitle>
            <DialogDescription>
              Configure the certification or training requirement for coaches.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., SafeSport Certification"
                className="mt-1"
                data-testid="req-name-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the requirement..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Valid For (months)</Label>
                <Input
                  type="number"
                  value={formData.validity_months}
                  onChange={(e) => setFormData({...formData, validity_months: parseInt(e.target.value) || 24})}
                  min={1}
                  max={120}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>External Training URL</Label>
              <Input
                type="url"
                value={formData.external_url}
                onChange={(e) => setFormData({...formData, external_url: e.target.value})}
                placeholder="https://safesport.org/"
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">Link to where coaches can complete the training</p>
            </div>
            
            <div>
              <Label>Instructions for Coaches</Label>
              <Textarea
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                placeholder="Instructions on how to complete and submit this requirement..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Required</p>
                <p className="text-xs text-slate-500">Coaches must complete this to be fully compliant</p>
              </div>
              <Switch
                checked={formData.required}
                onCheckedChange={(v) => setFormData({...formData, required: v})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-requirement-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingReq ? "Save Changes" : "Add Requirement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceSettings;
